import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, Tag } from 'lucide-react';
import { listBlogPosts } from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_e) {
    return '';
  }
};

/**
 * 2026-06-10 — Homepage preview of the latest 3 Hey Taxi! posts.
 * Renders nothing if the API is unreachable or there are zero published posts,
 * so the homepage never shows a broken section.
 */
const HeyTaxiPreview = ({ navigate }) => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listBlogPosts();
        if (!cancelled && Array.isArray(data)) {
          setPosts(data.slice(0, 3));
        }
      } catch (err) {
        // Silent — preview section just hides itself
        console.warn('Hey Taxi! preview unavailable:', err?.message || err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't render an empty/broken section.
  if (loaded && posts.length === 0) return null;

  return (
    <section
      className="py-20 sm:py-24 bg-white"
      data-testid="home-hey-taxi-preview"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
          <div>
            <span className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full mb-4">
              <BookOpen className="w-3.5 h-3.5" />
              Hey Taxi! Journal
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              Travel Tips, Updates &amp; Nassau Insight
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-3 max-w-2xl">
              Latest from <span className="font-semibold">Hey Taxi!</span> — practical advice for airport transfers, cruise port pickups, tours, and life on the road.
            </p>
          </div>
          <button
            onClick={() => (navigate ? navigate('/hey-taxi') : window.location.assign('/hey-taxi'))}
            data-testid="home-read-hey-taxi-btn"
            className="self-start sm:self-auto inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-6 py-3 rounded-lg font-semibold shadow transition whitespace-nowrap"
          >
            <span>Read Hey Taxi!</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {!loaded && (
          <p className="text-sm text-gray-500" data-testid="home-hey-taxi-preview-loading">Loading latest articles…</p>
        )}

        {loaded && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-hey-taxi-preview-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/hey-taxi/${post.slug}`}
                data-testid={`home-hey-taxi-card-${post.slug}`}
                className="group bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/9] bg-gradient-to-br from-cyan-100 via-blue-100 to-amber-100 overflow-hidden">
                  {post.imageUrl ? (
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-cyan-700/30" />
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-2">
                    <span className="inline-flex items-center gap-1 bg-cyan-50 border border-cyan-200 text-cyan-700 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
                      <Tag className="w-3 h-3" />
                      {post.category}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.publishedAt || post.createdAt)}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug mb-2 group-hover:text-cyan-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
                    {post.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-cyan-700 group-hover:text-cyan-800 font-semibold text-sm">
                    Read More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeyTaxiPreview;
